import React, { useState, useEffect, useCallback } from 'react';
import { formatPriceFR } from '../../utils/formatFR';

/**
 * Widget de matching Bien → Contacts Acquéreurs
 * Affiche les contacts dont les critères correspondent au bien
 */
const ContactMatchingWidget = ({ propertyId, variant = 'default' }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [propertySummary, setPropertySummary] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Charger les contacts correspondants
  const fetchMatches = useCallback(async () => {
    if (!propertyId) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/properties/matching/${propertyId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        setMatches(data.scored_matches || []);
        setPropertySummary(data.property_summary || null);
      }
    } catch (err) {
      console.error('Erreur matching contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [propertyId, API_URL]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  // Naviguer vers le contact
  const goToContact = (contactId) => {
    window.location.href = `/contacts/${contactId}`;
  };

  // Format du budget (FR complet : "100 000 € — 300 000 €")
  const formatBudget = (contact) => {
    const criteria = contact.criteres_recherche || {};
    const min = criteria.budget_min;
    const max = criteria.budget_max;

    if (min && max) {
      return `${formatPriceFR(min)} — ${formatPriceFR(max)}`;
    } else if (max) {
      return `≤ ${formatPriceFR(max)}`;
    } else if (min) {
      return `≥ ${formatPriceFR(min)}`;
    }
    return 'Budget NC';
  };

  // Obtenir les zones/villes recherchées (champ réel: zones_geographiques, fallback villes)
  const getVilles = (contact) => {
    const villes = contact.criteres_recherche?.zones_geographiques || contact.criteres_recherche?.villes || [];
    if (villes.length === 0) return 'Toutes zones';
    if (villes.length === 1) return villes[0];
    return `${villes[0]} +${villes.length - 1}`;
  };

  // Couleur du score
  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-blue-600 bg-blue-100';
    if (score >= 55) return 'text-amber-600 bg-amber-100';
    return 'text-slate-600 bg-slate-100';
  };

  // Variante compacte pour la page d'édition
  if (variant === 'compact') {
    return (
      <div className="border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-slate-700 flex items-center gap-1">
            <i className="bi bi-people-fill text-green-600"></i> Rapprochement acquéreurs
          </p>
          <div className="flex items-center gap-2">
            <button 
              onClick={fetchMatches}
              className="text-green-600 hover:text-green-700"
              title="Actualiser"
            >
              <i className={`bi bi-arrow-repeat text-xs ${loading ? 'animate-spin' : ''}`}></i>
            </button>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              matches.length > 0 ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-600'
            }`}>
              {loading ? '...' : matches.length}
            </span>
          </div>
        </div>
        
        {!propertyId ? (
          <div className="text-center py-4 text-slate-500 text-xs">
            <i className="bi bi-save block text-xl mb-1 opacity-50"></i>
            Enregistrez le bien pour voir les acquéreurs
          </div>
        ) : loading ? (
          <div className="text-center py-4 text-slate-500 text-xs">
            <i className="bi bi-arrow-repeat animate-spin block text-xl mb-1"></i>
            Recherche...
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-4 text-slate-500 text-xs">
            <i className="bi bi-person-x block text-xl mb-1 opacity-50"></i>
            Aucun acquéreur correspondant
          </div>
        ) : (
          <div className="space-y-2">
            {matches.slice(0, expanded ? matches.length : 3).map((match, idx) => {
              const contact = match.contact;
              return (
                <div 
                  key={idx}
                  className="bg-white rounded-lg p-2 border border-green-200 hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => goToContact(contact.id)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        match.score >= 85 ? 'bg-green-100' : match.score >= 70 ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        <i className={`bi bi-person-fill text-xs ${
                          match.score >= 85 ? 'text-green-600' : match.score >= 70 ? 'text-blue-600' : 'text-amber-600'
                        }`}></i>
                      </div>
                      <span className="text-xs font-semibold text-slate-800 truncate max-w-[100px]">
                        {contact.prenom} {contact.nom}
                      </span>
                    </div>
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getScoreColor(match.score)}`}>
                      {match.score}%
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-600 flex items-center gap-1">
                    <i className="bi bi-geo-alt-fill"></i> {getVilles(contact)} • {formatBudget(contact)}
                  </p>
                </div>
              );
            })}
            
            {matches.length > 3 && (
              <button 
                className="w-full text-center text-[10px] text-green-600 hover:text-green-700 font-semibold py-1"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <><i className="bi bi-chevron-up"></i> Réduire</>
                ) : (
                  <><i className="bi bi-plus-circle"></i> Voir {matches.length - 3} autres</>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Variante par défaut pour la page de consultation
  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
      <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
        <i className="bi bi-people-fill text-emerald-600"></i>
        Rapprochement acquéreurs
        <button 
          onClick={fetchMatches}
          className="ml-auto text-slate-400 hover:text-emerald-600 transition"
          title="Actualiser"
        >
          <i className={`bi bi-arrow-repeat ${loading ? 'animate-spin' : ''}`}></i>
        </button>
      </h3>
      <p className="text-xs text-slate-500 mb-3">Correspondances dans votre base</p>
      
      {!propertyId ? (
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-slate-300 mb-2">-</div>
          <p className="text-sm text-slate-600">Enregistrez le bien pour voir les acquéreurs</p>
        </div>
      ) : loading ? (
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <i className="bi bi-arrow-repeat animate-spin text-3xl text-emerald-400 mb-2 block"></i>
          <p className="text-sm text-slate-600">Recherche des acquéreurs...</p>
        </div>
      ) : matches.length === 0 ? (
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-slate-300 mb-2">0</div>
          <p className="text-sm text-slate-600 mb-3">Aucun acquéreur correspondant pour le moment</p>
          <button 
            onClick={fetchMatches}
            className="w-full px-4 py-2 bg-emerald-600/80 hover:bg-emerald-600 text-white text-sm rounded-lg transition"
          >
            <i className="bi bi-arrow-repeat mr-2"></i>
            Actualiser le matching
          </button>
        </div>
      ) : (
        <>
          <div className="bg-emerald-50 rounded-lg p-3 mb-4 text-center">
            <div className="text-3xl font-bold text-emerald-600 mb-1">{matches.length}</div>
            <p className="text-sm text-emerald-700">acquéreur{matches.length > 1 ? 's' : ''} correspondant{matches.length > 1 ? 's' : ''}</p>
          </div>
          
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {matches.map((match, idx) => {
              const contact = match.contact;
              return (
                <div 
                  key={idx}
                  className="p-3 bg-slate-50 hover:bg-emerald-50 rounded-lg border border-slate-200 hover:border-emerald-300 transition cursor-pointer"
                  onClick={() => goToContact(contact.id)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        match.score >= 85 ? 'bg-green-100' : match.score >= 70 ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        <i className={`bi bi-person-fill ${
                          match.score >= 85 ? 'text-green-600' : match.score >= 70 ? 'text-blue-600' : 'text-amber-600'
                        }`}></i>
                      </div>
                      <div>
                        <div className="font-semibold text-slate-800">
                          {contact.prenom} {contact.nom}
                        </div>
                        <div className="text-xs text-slate-500">
                          {contact.type_contact || 'Acquéreur'}
                        </div>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-sm font-bold ${getScoreColor(match.score)}`}>
                      {match.score}%
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-600">
                    <span className="flex items-center gap-1">
                      <i className="bi bi-geo-alt"></i> {getVilles(contact)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="bi bi-currency-euro"></i> {formatBudget(contact)}
                    </span>
                  </div>
                  {match.match_details && match.match_details.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {match.match_details.map((detail, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] inline-flex items-center gap-0.5">
                          <i className="bi bi-check-lg"></i> {detail}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ContactMatchingWidget;
